import { media, styled } from "@iventis/styles";
import React, { FunctionComponent, useEffect } from "react";
import { FolderBrowserView, NodeServices, TreeBrowserDomainNodeContext, TreeBrowserState, createInitialState, useTreeBrowserInitialise } from "@iventis/tree-browser";
import { Node } from "@iventis/domain-model/model/node";
import { userLocalStorageItems, userLocalStorageManager } from "@iventis/local-storage/src/user-local-storage";
import LibraryComponent from "./library";
import { getExpandedNodeId, getMainNodeId, getSort } from "./library-get-initial-values";
import { libraryWebSockets } from "./library.machine.web-sockets";
import { libraryURLStateMiddleware } from "./library.machine.middleware";
import { AppBarSubMenuNode, StaticLibrary, isStaticLibrary } from "./mapping-library.helpers";

interface MapBrowserProps {
    libraries: AppBarSubMenuNode[];
    selectedLibraryNode: Node;
    nodeServices: NodeServices<Node>;
    userId: string;
    staticLibrary?: StaticLibrary;
    allowTreeActions: boolean;
    onLibraryChange: (newTree: Node, mainNodeId: string) => void;
}

const MapBrowserComponent: FunctionComponent<MapBrowserProps> = ({ libraries, selectedLibraryNode, nodeServices, userId, staticLibrary, allowTreeActions, onLibraryChange }) => {
    const getInitialTreeBrowserState = (fallbackLibraryId: string): TreeBrowserState<Node> => {
        const selectLibraryId = selectedLibraryNode.id ?? fallbackLibraryId;
        return createInitialState({ sort: getSort(userId), mainNodeId: getMainNodeId(selectLibraryId), treeId: selectLibraryId, expandedNodeIds: getExpandedNodeId() });
    };

    const [treeBrowserState, treeBrowserContext] = useTreeBrowserInitialise(getInitialTreeBrowserState(libraries[0].id), nodeServices, libraryWebSockets, undefined, {
        middleware: selectedLibraryNode?.id != null && !isStaticLibrary(selectedLibraryNode.id) ? libraryURLStateMiddleware : null, // We do not want URL functionality if static library
    });

    useEffect(() => {
        if (treeBrowserState?.tree?.id != null && treeBrowserContext.currentState?.tree?.id !== selectedLibraryNode?.id) {
            treeBrowserContext.initialise(getInitialTreeBrowserState(selectedLibraryNode.id), nodeServices);
        }
    }, [selectedLibraryNode?.id]);

    return (
        <>
            {treeBrowserState && (
                <TreeBrowserDomainNodeContext.Provider value={treeBrowserContext}>
                    <StyledMapBrowser data-cy="map-browser">
                        <LibraryComponent
                            allowTreeActions={allowTreeActions}
                            libraries={libraries}
                            allowedViewTypes={[FolderBrowserView.List, FolderBrowserView.Tile]}
                            defaultViewType={(userLocalStorageManager.get(userLocalStorageItems.folderBrowserView, userId) as FolderBrowserView) || FolderBrowserView.Tile}
                            userId={userId}
                            staticLibrary={staticLibrary}
                            onLibraryChange={onLibraryChange}
                        />
                    </StyledMapBrowser>
                </TreeBrowserDomainNodeContext.Provider>
            )}
        </>
    );
};

const StyledMapBrowser = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1 1;
    overflow: hidden;
    position: relative;
    ${media.appAlongBase} {
        height: 100%;
        // Allows for the navigation bar at the bottom
        max-height: calc(100vh - 49px);
        max-height: calc(100dvh - 49px);
    }
`;

export default MapBrowserComponent;
